import { v3 } from "../short3";
import * as Math3 from "../Maths3";
import { OBB } from "three/examples/jsm/math/OBB";
import { box3AndMatrixToOBB } from "../Maths3";

import * as _ from "lodash";
import * as Scenes from "../Scenes";
import { Box3, Matrix4, Object3D, Ray, Raycaster, Vector3 } from "three";

const DEFAULT_AXIS = ["x", "y", "z"];

/**
 * Statische Utility-Klasse zu den Themen Objekte und Collisionen, vor allem bezüglich Boxen und Rays.
 */
export class Collisions {
  /**
   * Gibt den Collisionspunkt eines Rays mit einem Objekt zurück, oder null
   */
  static rayPoint(obj: Object3D, origin: Vector3, direction: Vector3): Vector3 | null {
    const raycaster = new Raycaster(origin, direction);
    const intersects = raycaster.intersectObject(obj, true);
    const intersection = _.head(intersects);
    if (intersection) {
      return intersection.point;
    }
    return null;
  }

  /**
   * Gibt den Min- und Max- Kollisionspunkt einer Linie mit einem Objekt zurück,
   * so als ob man das Objekt mit einem Speer durchsticht und die beiden Einstichlöcher zurückgibt.
   */
  static minMaxCollisionsOnLine(
    object: Object3D,
    position: Vector3,
    direction: Vector3,
    offset: number = 10000,
  ): [Vector3 | null, Vector3 | null] {
    return [
      Collisions.rayPoint(
        object,
        position.clone().add(direction.clone().multiplyScalar(-offset)),
        direction.clone(),
      ),
      Collisions.rayPoint(
        object,
        position.clone().add(direction.clone().multiplyScalar(offset)),
        direction.clone().negate(),
      ),
    ];
  }

  /**
   * Ermittelt die Teilmenge aller Vertices eines Objekt innerhalb einer Box und gibt deren BoundingBox zurück.
   * @param object
   * @param box
   * @returns box
   */
  static boxFromObjectVerticesInsideBox(object: Object3D, box: Box3): Box3 {
    const b = new Box3();
    Scenes.traverseVertices(object, (v: Vector3) => {
      if (box.containsPoint(v)) {
        b.expandByPoint(v);
      }
    });
    return b;
  }

  /**
   * Misst aus, wie dick das Objekt bezüglich einer Linie ist,
   * so als ob man das Objekt mit einem Speer durchsticht und die Entfernung zwischen den beiden Einstichlöchern misst.
   */
  static sizeOfObjectOnLine(
    object: Object3D,
    position: Vector3,
    direction: Vector3,
    offset: number = 10000,
  ): number {
    const [a, b] = Collisions.minMaxCollisionsOnLine(object, position, direction, offset);
    if (a == null || b == null) {
      return 0;
    }
    return a.distanceTo(b);
  }

  /**
   * Verschiebt eine Box3 auf einem Ray vom Ray-Origin aus so weit wie möglich weg,
   * bis die Box einen der Points berührt.
   * Der Mittelpunkt der Box liegt auf dem Ray.
   *
   * <img src="content/getBoxPositionOnRayWhereItTouchesNearestPoint.png">
   *
   * @return Zurückgegeben wird die Mittelpunkts-Position der Box, wo sie einen der Punkte berühren würde, oder
   * null, falls die Box keinen der Punkte berühren würde, also bis ins Unendliche verschoben werden könnte.
   */
  static getBoxPositionOnRayWhereItTouchesNearestPoint(
    box: Box3,
    ray: Ray,
    points: Vector3[],
  ): Vector3 | null {
    const antiray = ray.clone();
    antiray.direction.multiplyScalar(-1);
    const centerNew = ray.origin.clone();
    const centerOld = box.getCenter(v3());
    centerNew.sub(centerOld);
    const testBox = box.clone();
    testBox.min.add(centerNew);
    testBox.max.add(centerNew);

    const minDistanceFromOrigin = _.min(
      points.map((p) => {
        antiray.origin.copy(p);
        const i = antiray.intersectBox(testBox, v3());
        return i ? i.distanceTo(p) : Number.POSITIVE_INFINITY;
      }),
    );

    if (minDistanceFromOrigin === undefined || !Number.isFinite(minDistanceFromOrigin)) {
      return null;
    }

    return ray.direction.clone().multiplyScalar(minDistanceFromOrigin).add(ray.origin);
  }

  /**
   * Gibt die größte mögliche Box3 zurück, die um einen Center-Punkt herum möglich ist, ohne
   * mit CollisionObjects zu kollidieren. Es wird nur nach Kollisionen entlang der Strahlen
   * durch den Center parallel zu den Koordinatenachsen geschaut.
   *
   * <img src="content/getCollisionBox.png">
   *
   * Die zurückgegebene Box3 ist maximal so groß wie maxBox.
   */
  static getCollisionBox(
    center: Vector3,
    collisionObjects: Object3D[],
    maxBox: Box3,
    axis: string[] = DEFAULT_AXIS,
  ): Box3 {
    if (!maxBox.containsPoint(center)) {
      throw new Error("maxBox must contain center");
    }
    const box = maxBox.clone();
    axis.forEach((a) => {
      [
        [-1, "min"],
        [+1, "max"],
      ].forEach((p) => {
        const sign = p[0] as number;
        const prop = p[1] as "min" | "max";
        const direction = v3();
        (direction as any)[a] = sign;
        const raycaster = new Raycaster(center, direction);
        const intersects = raycaster.intersectObjects(collisionObjects, true);
        const intersection = _.head(intersects);
        if (intersection) {
          (box[prop] as any)[a] = (intersection.point as any)[a];
        }
      });
    });
    return box.intersect(maxBox);
  }

  /**
   * Funktioniert ähnlich wie getCollisionBox, aber gibt die jeweils nächsten CollisionObjects zurück,
   * die von den achsenparallelen Strahlen getroffen werden. Center-Punkt ist die Position des Objects (1. Pameter).
   * Zurückgegeben wird ein Array von Objekten.
   */
  static getCollisionObjects(
    object: Object3D,
    collisionObjects: Object3D[],
    axis: string[] = DEFAULT_AXIS,
  ): Object3D[] {
    const objects: Object3D[] = [];
    const coll = collisionObjects.filter((o) => o !== object);

    axis.forEach((a) => {
      [
        [-1, "min"],
        [+1, "max"],
      ].forEach((p) => {
        const sign = p[0] as number;
        // const prop = p[1]; // unused
        const direction = v3();
        (direction as any)[a] = sign;
        const raycaster = new Raycaster(object.position, direction);
        const intersects = raycaster.intersectObjects(coll, true);
        const intersection = _.head(intersects);
        if (intersection) {
          objects.push(intersection.object);
        }
      });
    });
    return objects;
  }

  /**
   * Sendet von der Oberfläche einer Box Strahlen aus, die auch einer Geraden durch den Mittelpunkt der Box gehen
   * und jeweils achsenparallel sind, und gibt die getroffenen CollsionObjects zurück.
   * Rückgabe ist ein Object, in dem zu jeder Achse und Richtung entweder das erste getroffene Object steht oder nichts.
   *
   * <img src="content/getBorderObjects.png">
   *
   * @example
   * //Beispiel für die Rückgabe der Zeichnung wäre (hier nur ein Objekt):
   * {'y': {'max':CollisionObject13 } }
   */
  static getBorderObjects(
    box: Box3,
    collisionObjects: Object3D[],
    axis: string[] = DEFAULT_AXIS,
    offset: Vector3 = Math3.vector.zero,
  ): Record<string, Record<string, Object3D>> {
    const objects: Record<string, any> = {};
    axis.forEach((a) => {
      objects[a] = {};
      [
        [-1, "min"],
        [+1, "max"],
      ].forEach((p) => {
        // In jeder Richtung wird der Punkt auf der Mitte der Grenzfläche, ein Stück Richtung Zentrum, berechnet.
        // Der Strahl von dort aus nach aussen zeigt auf das BorderObject.
        const sign = p[0] as number;
        const prop = p[1] as string;
        const direction = v3();
        (direction as any)[a] = sign;
        const point = box.getCenter(v3()).add(offset);
        (point as any)[a] = (box as any)[prop][a] - sign * 0.1;
        const raycaster = new Raycaster(point, direction);
        const intersects = raycaster.intersectObjects(collisionObjects, true);
        const intersection = _.head(intersects);
        if (intersection) {
          objects[a][prop] = intersection.object;
        }
      });
    });
    return objects;
  }

  /**
   * Ermittelt alle Collisionspaare per O(n^2) Vergleich für eine Liste von Objekten,
   * die mittels THREE.OBB (oriented Boxen) Collisionen getestet werden
   * @param {Object[]} objects Liste von Objekten
   * @param {function} localBox3Getter gibt zu einem Objekt die lokale Box3 (im eigenen Koordinatensystem) zurück
   * @param {function} matrixWorldGetter gibt zu einem Objekt die MatrixWorld zurück
   * @param {function} accept gibt zu zwei Objekten zurück, ob sie getestet werden sollen
   * @returns {*[]} Liste von [a,b] Objektpaaren, die collidieren
   */
  static bruteForceOBBoxCollisions<T extends Object3D>(
    objects: T[],
    {
      localBox3Getter = (o: T) =>
        Scenes.boundingBoxLocalMatrix(o, (o: any) => true, new Matrix4(), true),
      matrixWorldGetter = (o: T) => {
        o.updateMatrixWorld();
        return o.matrixWorld;
      },
      accept = (a: T, b: T) => true,
    } = {},
  ): [T, T][] {
    const boxes = objects.map((o) => {
      let box3Local: Box3, matrixWorld: Matrix4;
      const ensureBoxAndMatrix = () => {
        if (!box3Local || !matrixWorld) {
          box3Local = localBox3Getter(o);
          matrixWorld = matrixWorldGetter(o);
        }
      };

      let aab: Box3, obb: OBB;
      return {
        aab: () => {
          if (aab == null) {
            ensureBoxAndMatrix();
            aab = box3Local.clone().applyMatrix4(matrixWorld);
          }
          return aab;
        },
        obb: () => {
          if (obb == null) {
            ensureBoxAndMatrix();
            obb = box3AndMatrixToOBB(box3Local, matrixWorld);
          }
          return obb;
        },
      };
    });
    const collisions: [T, T][] = [];
    for (let i = 0; i < objects.length - 1; i++) {
      for (let j = i + 1; j < objects.length; j++) {
        if (
          objects[i] !== objects[j] &&
          accept(objects[i], objects[j]) &&
          boxes[i].aab().intersectsBox(boxes[j].aab()) &&
          boxes[i].obb().intersectsOBB(boxes[j].obb())
        ) {
          collisions.push([objects[i], objects[j]]);
        }
      }
    }
    return collisions;
  }
}
